import React, { useEffect, useState } from 'react';
import { Bell, User, Search, Settings, Shield, LogOut, Gift, CreditCard, HelpCircle, ChevronDown, Crown, Sun, Moon, Star, Lock, Users, MoreVertical, Plus, Eye, DollarSign, Sparkles } from 'lucide-react';
import { useTheme } from './ThemeContext';
import { ProfileToggle } from './ProfileToggle';
import { HelpCenter } from './help/HelpCenter';
import { TokenStore } from './TokenStore';
import { ProfileDrawer } from './ProfileDrawer';
import { RitualTrackerWidget } from './rituals/RitualTrackerWidget';
import { MonetizationBoard } from './monetization/MonetizationBoard';
interface TopHeaderProps {
  tokenCount: number;
  notificationCount: number;
  onNotificationsClick: () => void;
  onNavigate: (section: string) => void;
  activeSection: string;
  onOpenSettings: () => void;
}
export const TopHeader: React.FC<TopHeaderProps> = ({
  tokenCount,
  notificationCount,
  onNotificationsClick,
  onNavigate,
  activeSection,
  onOpenSettings
}) => {
  const {
    theme,
    toggleTheme
  } = useTheme();
  const [showProfileDrawer, setShowProfileDrawer] = useState(false);
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const [showTokenStore, setShowTokenStore] = useState(false);
  const [showMonetizationBoard, setShowMonetizationBoard] = useState(false);
  const [isHoveringProfileToggle, setIsHoveringProfileToggle] = useState(false);
  // Debug the hover state with console log
  useEffect(() => {
    console.log('Profile toggle hover state:', isHoveringProfileToggle);
  }, [isHoveringProfileToggle]);
  // Logo URLs for different themes
  const lightLogo = 'https://uploadthingy.s3.us-west-1.amazonaws.com/87n6jNX3rfeGzuoiM2LA8A/sinshift_ONLYFANS_png.png';
  const darkLogo = 'https://uploadthingy.s3.us-west-1.amazonaws.com/nJcgHVcg3nLPd525puwYH1/image.png';
  // Determine which profile is active based on the activeSection
  const activeProfile = activeSection === 'creator_dashboard' ? 'angel' : 'customer';
  // Toggle function for profile switching
  const handleProfileToggle = (profile: 'angel' | 'customer') => {
    if (profile === 'angel') {
      onNavigate('creator_dashboard');
    } else {
      onNavigate('social_feed');
    }
  };
  // Handle help center
  const handleHelpCenter = () => {
    setShowHelpCenter(true);
  };
  // Handle token store
  const handleOpenTokenStore = () => {
    setShowTokenStore(true);
  };
  const handleCloseTokenStore = () => {
    setShowTokenStore(false);
  };
  const handlePurchase = (bundleId: string) => {
    console.log(`Purchased bundle: ${bundleId}`);
    setShowTokenStore(false);
  };
  // Handle ritual board navigation
  const handleOpenRitualBoard = () => {
    onNavigate('rituals');
  };
  // Handle monetization board
  const handleOpenMonetizationBoard = () => {
    setShowMonetizationBoard(true);
  };
  const handleCloseMonetizationBoard = () => {
    setShowMonetizationBoard(false);
  };
  return <header className="fixed top-0 left-0 right-0 z-20 bg-white dark:bg-gray-900 shadow-sm border-b border-of-gray dark:border-gray-800" data-id="element-21433">
      <div className="flex items-center justify-between p-3 px-4" data-id="element-21434">
        {/* Left section with logo and profile toggle */}
        <div className="flex items-center gap-3" data-id="element-21435">
          <img src={theme === 'dark' ? 'https://uploadthingy.s3.us-west-1.amazonaws.com/oHLwgX3v31LEz6CJrxRSjZ/SinShift_logo_PNG.png' : lightLogo} alt="SinShift" className={`h-8 ${theme === 'dark' ? 'h-10' : 'h-8'}`} data-id="element-21436" />
          {/* Profile toggle buttons directly in the header */}
          <div className={`relative flex items-center bg-of-gray dark:bg-gray-800 rounded-md overflow-visible ${isHoveringProfileToggle ? 'ring-2 ring-green-500' : ''}`} onMouseEnter={() => setIsHoveringProfileToggle(true)} onMouseLeave={() => setIsHoveringProfileToggle(false)} data-id="element-21437">
            <button onClick={() => handleProfileToggle('customer')} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${activeProfile === 'customer' ? 'bg-of-blue dark:bg-gradient-to-r dark:from-pink-600 dark:to-purple-600 text-white' : 'bg-transparent text-of-text-secondary dark:text-gray-400'}`} data-id="element-21438">
              <Users size={16} data-id="element-21439" />
              <span className="font-medium" data-id="element-21440">Customer</span>
            </button>
            <button onClick={() => handleProfileToggle('angel')} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${activeProfile === 'angel' ? 'bg-of-blue dark:bg-gradient-to-r dark:from-pink-600 dark:to-purple-600 text-white' : 'bg-transparent text-of-text-secondary dark:text-gray-400'}`} data-id="element-21441">
              <Star size={16} data-id="element-21442" />
              <span className="font-medium" data-id="element-21443">Angel</span>
            </button>
            {/* Monetization button that appears on hover */}
            {isHoveringProfileToggle && <button onClick={handleOpenMonetizationBoard} className="fixed top-12 left-40 flex items-center justify-center px-3 py-1.5 bg-gradient-to-r from-green-400 to-green-600 text-white rounded-md shadow-lg z-50" style={{
            pointerEvents: 'auto'
          }} data-id="element-21444">
                <DollarSign size={16} className="mr-1" data-id="element-21445" />
                <span className="text-sm font-medium" data-id="element-21446">Monetize</span>
                <Sparkles size={10} className="ml-1 text-yellow-200 animate-pulse" data-id="element-21447" />
              </button>}
          </div>
          {/* Keep the empty ProfileToggle component for backward compatibility */}
          <ProfileToggle activeProfile={activeProfile} onToggle={handleProfileToggle} data-id="element-21448" />
        </div>
        {/* Center section - now empty */}
        <div className="flex items-center justify-center" data-id="element-21449">
          {/* Empty space in the center */}
        </div>
        {/* Right section with actions */}
        <div className="flex items-center gap-3 w-auto justify-end flex-shrink-0" data-id="element-21450">
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-of-gray dark:bg-gray-800 rounded-full" data-id="element-21451">
            <Search size={16} className="text-of-text-secondary dark:text-gray-400" data-id="element-21452" />
            <input type="text" placeholder="Search..." className="bg-transparent border-none outline-none text-sm w-32 dark:text-gray-300" data-id="element-21453" />
          </div>
          <button className="hidden md:flex items-center gap-1.5 px-4 py-1.5 bg-of-blue dark:bg-gradient-to-r dark:from-pink-600 dark:to-purple-600 rounded-md text-white font-medium text-sm hover:bg-of-blue-dark dark:hover:from-pink-600/80 dark:hover:to-purple-600/80 transition-colors" onClick={() => onNavigate('advanced_token')} data-id="element-21454">
            <span data-id="element-21455">Serious Players Use DesireGems 💎</span>
          </button>
          {/* Ritual Tracker Widget */}
          <RitualTrackerWidget onOpenRitualBoard={handleOpenRitualBoard} data-id="element-21456" />
          <div className="flex items-center" data-id="element-21457">
            <button className="flex items-center gap-1 px-3 py-1.5 bg-of-gray dark:bg-gray-800 rounded-l-md text-of-text-primary dark:text-white" data-id="element-21458">
              <Gift size={16} data-id="element-21459" />
              <span className="text-sm font-medium" data-id="element-21460">{tokenCount}</span>
            </button>
            <button onClick={handleOpenTokenStore} className="flex items-center justify-center h-full px-1.5 bg-of-blue dark:bg-pink-600 text-white rounded-r-md hover:bg-of-blue-dark dark:hover:bg-pink-700 transition-all shadow-sm" data-id="element-21461">
              <Plus size={14} strokeWidth={2.5} data-id="element-21462" />
            </button>
          </div>
          <button className="p-2 rounded-full hover:bg-of-gray dark:hover:bg-gray-800" onClick={toggleTheme} data-id="element-21463">
            {theme === 'dark' ? <Sun size={18} className="text-gray-400 hover:text-white transition-colors" data-id="element-21464" /> : <Moon size={18} className="text-of-text-secondary hover:text-of-text-primary transition-colors" data-id="element-21465" />}
          </button>
          <button className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-md p-2 hover:bg-of-gray dark:hover:bg-gray-700" onClick={() => setShowProfileDrawer(true)} data-id="element-21466">
            <User size={18} className="text-black dark:text-white" strokeWidth={1.5} data-id="element-21467" />
            <span className="text-black dark:text-white font-medium text-xs" data-id="element-21468">
              Profile
            </span>
            <MoreVertical size={18} className="text-black dark:text-white ml-1" data-id="element-21469" />
          </button>
        </div>
      </div>
      {/* Profile Drawer */}
      <ProfileDrawer isOpen={showProfileDrawer} onClose={() => setShowProfileDrawer(false)} onNavigate={onNavigate} onOpenSettings={onOpenSettings} onOpenHelpCenter={handleHelpCenter} tokenCount={tokenCount} messageCount={3} data-id="element-21470" />
      {/* Help Center Modal */}
      <HelpCenter isOpen={showHelpCenter} onClose={() => setShowHelpCenter(false)} data-id="element-21471" />
      {/* Token Store Modal */}
      {showTokenStore && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-21472">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-auto bg-white dark:bg-black rounded-lg shadow-2xl" data-id="element-21473">
            <TokenStore onBack={handleCloseTokenStore} onPurchase={handlePurchase} currentTokens={tokenCount} data-id="element-21474" />
          </div>
        </div>}
      {/* Monetization Board Modal */}
      {showMonetizationBoard && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-21475">
          <div className="w-full h-full max-w-full overflow-hidden bg-white dark:bg-gray-900 shadow-2xl flex" data-id="element-21476">
            <MonetizationBoard onClose={handleCloseMonetizationBoard} data-id="element-21477" />
          </div>
        </div>}
    </header>;
};